import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSun, faMoon, faExpand, faCompress } from '@fortawesome/free-solid-svg-icons';
import { logOut } from '../../contexts/loginContexts';
import config from '../../config';
import { getStoredAuthUser } from '../../utils/authUser';

const buildInitials = (value) => {
  const parts = String(value || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);

  if (!parts.length) return 'U';
  return parts.map((item) => item[0]?.toUpperCase() || '').join('') || 'U';
};

const getImageCandidates = (path) => {
  if (!path) return [];

  const normalizedPath = String(path).trim().replace(/\\/g, '/');
  if (!normalizedPath) return [];

  if (
    normalizedPath.startsWith('blob:') ||
    normalizedPath.startsWith('data:') ||
    normalizedPath.startsWith('http://') ||
    normalizedPath.startsWith('https://')
  ) {
    return [normalizedPath];
  }

  let cleanPath = normalizedPath.startsWith('~')
    ? normalizedPath.substring(1)
    : normalizedPath;

  if (!cleanPath.startsWith('/')) {
    cleanPath = `/${cleanPath}`;
  }

  cleanPath = cleanPath.replace(/\/{2,}/g, '/');

  let apiBase = '';
  let serverOrigin = '';

  try {
    const url = new URL(config.baseurl, window.location.origin);
    apiBase = url.href.replace(/\/+$/, '');
    serverOrigin = url.origin;
  } catch {
    apiBase = String(config.baseurl || window.location.origin).replace(/\/+$/, '');
    serverOrigin = window.location.origin;
  }

  const candidates = [
    `${apiBase}${cleanPath}`,
    `${serverOrigin}${cleanPath}`,
  ];

  return [...new Set(candidates.filter(Boolean))];
};

const Navbar = ({ isDropdownOpen, setIsDropdownOpen }) => {
  const navigate = useNavigate();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [user, setUser] = useState(() => getStoredAuthUser());
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Get theme preference from localStorage or default to light mode
    const savedTheme = localStorage.getItem('theme');
    return savedTheme ? savedTheme === 'dark' : false;
  });

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);

  const userDisplayName = useMemo(
    () => user?.fullName || user?.userName || user?.email || 'User',
    [user]
  );
  const userDisplayEmail = useMemo(
    () => user?.workEmail || user?.email || 'No email',
    [user]
  );
  const photoCandidates = useMemo(
    () => getImageCandidates(user?.photoUrlWithPath),
    [user?.photoUrlWithPath]
  );

  // Close the user menu when clicking outside of it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('#dropdown-user') && !event.target.closest('.user-menu')) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const syncUser = () => {
      setUser(getStoredAuthUser());
    };

    syncUser();
    window.addEventListener('storage', syncUser);

    return () => {
      window.removeEventListener('storage', syncUser);
    };
  }, []);

  useEffect(() => {
    setPhotoIndex(0);
  }, [user?.photoUrlWithPath]);

  const logout = async () => {
    // tokens are strings, DO NOT parse
    const refreshToken = localStorage.getItem("refreshToken");

    const payload = {
      UserId: user?.userId,
      RefreshToken: refreshToken
    };

    try {
      const response = await logOut(`${config.baseurl}Auth/logOut`, payload);

      // Axios automatically parses JSON
      if (response?.status === 200 || response?.message === "Logged out successfully") {
        localStorage.removeItem('authtoken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        localStorage.removeItem('tokenExpiry');
        navigate('/login', { replace: true });
      } else {

      }
    } catch (err) {
      console.error(err);
    }
  };

  // Function to toggle dark mode
  const toggleDarkMode = () => {
    setIsDarkMode((prev) => !prev);
  };

  // Update body class based on the theme
  useEffect(() => {
    const bodyClass = isDarkMode ? 'dark' : 'light';
    document.body.className = bodyClass;
    localStorage.setItem('theme', bodyClass);
  }, [isDarkMode]);

  // Function to toggle fullscreen mode
  const toggleFullscreen = () => {
    if (!isFullscreen) {
      document.documentElement.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
    setIsFullscreen((prev) => !prev);
  };

  const handleProfileImageError = () => {
    setPhotoIndex((current) => {
      if (current + 1 < photoCandidates.length) {
        return current + 1;
      }

      return current;
    });
  };

  const shouldShowImage = photoCandidates.length > 0 && photoIndex < photoCandidates.length;

  return (
    <nav className={`fixed top-0 z-50 w-full bg-white dark:bg-gray-800 dark:border-b dark:border-gray-700`}>
      <div className="px-3 py-3 lg:px-5 lg:pl-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center justify-start rtl:justify-end">
            <button
              onClick={() => setIsDropdownOpen((prev) => !prev)}
              data-drawer-target="logo-sidebar"
              data-drawer-toggle="logo-sidebar"
              aria-controls="logo-sidebar"
              type="button"
              className="navbar-toggle inline-flex items-center p-2 text-sm text-gray-500 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:text-gray-400 dark:hover:bg-gray-700 dark:focus:ring-gray-600"
            >
              {/* {!isDropdownOpen ? (
                 <svg
                 className="w-6 h-6"
                 aria-hidden="true"
                 fill="currentColor"
                 viewBox="0 0 20 20"
                 xmlns="http://www.w3.org/2000/svg"
               >
                 <path
                   clipRule="evenodd"
                   fillRule="evenodd"
                   d="M2 4.75A.75.75 0 012.75 4h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 4.75zm0 10.5a.75.75 0 01.75-.75h7.5a.75.75 0 010 1.5h-7.5a.75.75 0 01-.75-.75zM2 10a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 10z"
                 ></path>
               </svg>
              ) : (
                // Close sidebar icon
                <svg
                  className="w-6 h-6"
                  aria-hidden="true"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    clipRule="evenodd"
                    fillRule="evenodd"
                    d="M10 9.293l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 011.414-1.414L10 9.293z"
                  ></path>
                </svg>
                // Open sidebar icon
               
              )} */}

              <svg
                className="w-6 h-6"
                aria-hidden="true"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  clipRule="evenodd"
                  fillRule="evenodd"
                  d="M2 4.75A.75.75 0 012.75 4h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 4.75zm0 10.5a.75.75 0 01.75-.75h7.5a.75.75 0 010 1.5h-7.5a.75.75 0 01-.75-.75zM2 10a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 10z"
                ></path>
              </svg>
            </button>
            <Link to="/dashboard" className="flex ms-2 md:me-24">
              <img src="/light.png" className="h-8 me-3" alt="Bangla Fire Logo" />
            </Link>
          </div>
          <div className="flex items-center">
            {/* Dark/Light mode toggle button */}
            <button
              onClick={toggleDarkMode}
              className="p-2 text-gray-500 dark:text-gray-400"
            >
              <FontAwesomeIcon icon={isDarkMode ? faSun : faMoon} className="w-5 h-5" />
            </button>
            {/* Fullscreen toggle button */}
            <button
              onClick={toggleFullscreen}
              className="p-2 text-gray-500 dark:text-gray-400 ms-3"
            >
              <FontAwesomeIcon icon={isFullscreen ? faCompress : faExpand} className="w-5 h-5" />
            </button>
            <div className="relative flex items-center ms-3 user-menu">
              <button
                type="button"
                onClick={() => setIsUserMenuOpen((prev) => !prev)}
                className="flex items-center gap-3 rounded-full p-1 transition hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <div className="flex items-center">
                  {shouldShowImage ? (
                    <img
                      className="w-9 h-9 rounded-full cursor-pointer object-cover"
                      src={photoCandidates[photoIndex]}
                      alt={userDisplayName}
                      onError={handleProfileImageError}
                    />
                  ) : (
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-900 text-xs font-semibold text-white dark:bg-slate-600">
                      {buildInitials(userDisplayName)}
                    </div>
                  )}
                </div>
                <div className="hidden text-left sm:block">
                  <div className="max-w-[160px] truncate text-sm font-semibold text-gray-800 dark:text-white">
                    {userDisplayName}
                  </div>
                  <div className="max-w-[180px] truncate text-xs text-gray-500 dark:text-gray-300">
                    {userDisplayEmail}
                  </div>
                </div>
              </button>
              {isUserMenuOpen && (
                <div className="absolute right-0 z-50 mt-[247px] text-base list-none bg-white divide-y divide-gray-100 rounded shadow dark:bg-gray-700 dark:divide-gray-600" id="dropdown-user">
                  <div className="px-4 py-3" role="none">
                    <p className="text-sm text-gray-900 dark:text-white" role="none">{userDisplayName}</p>
                    <p className="text-sm font-medium text-gray-900 truncate dark:text-gray-300" role="none">{userDisplayEmail}</p>
                  </div>
                  <ul className="py-1" role="none">
                    <li>
                      <Link to="/dashboard" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-600 dark:hover:text-white" role="menuitem">Dashboard</Link>
                    </li>
                    <li>
                      <Link to="/profile" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-600 dark:hover:text-white" role="menuitem">Profile</Link>
                    </li>
                    <li>
                      <button
                        type="button"
                        onClick={logout}
                        className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-600 dark:hover:text-white"
                        role="menuitem"
                      >
                        Sign out
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
